/* eslint-disable no-param-reassign */
import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import { filterType } from '../../config/enum';
import { FilterUpdateProperties } from '../../interface/filter.interface';
import {
  APIInvoiceResponseInterface,
  InvoiceInitialProperties,
  RequestInvoiceInterface,
} from '../../interface/invoice-interface';
import {
  ClearFilters,
  RemoveSingleFilterFromList,
  UpdateCommonFilter,
} from '../../shared-functions/CommonFilters';
import invoiceApi from '../../utils/api/invoice';

import { setNotificationMessage } from './NotificationManagementSlice';

/**
 * Fetch invoice list filters
 */
export const fetchInvoiceFilters = createAsyncThunk<any, void>(
  'invoice/fetchInvoiceFilters',
  /**
   * Async callback to fetch invoice filters
   * @param {void} _ - Void arg
   * @param {any} thunkAPI - Thunk API
   * @param {any} thunkAPI.dispatch - Dispatch function
   * @returns {Promise<any>} Response data
   */
  async (_, { dispatch }) => {
    try {
      const response = await invoiceApi.fetchInvoiceFilters();
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch invoice filters',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  }
);

/**
 * Fetch invoice list
 */
export const fetchInvoiceList = createAsyncThunk<APIInvoiceResponseInterface, RequestInvoiceInterface>(
  'invoice/fetchInvoiceList',
  /**
   * Async callback to fetch invoice list
   * @param {RequestInvoiceInterface} data - Request payload
   * @param {any} thunkAPI - Thunk API
   * @param {any} thunkAPI.dispatch - Dispatch function
   * @returns {Promise<APIInvoiceResponseInterface>} Response data
   */
  async (data, { dispatch }) => {
    try {
      const response = await invoiceApi.fetchInvoiceList(data);
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to fetch invoice list',
          code: error?.response?.data?.code || 500,
          status: true,
        }),
      );
      throw error;
    }
  }
);

const initialState: InvoiceInitialProperties = {
  invoices: [],
  limit: 20,
  page: 1,
  totalPages: 1,
  totalResults: 0,
  loading: false,
  filter: {},
  filters: [
    {
      name: 'Search here...',
      key: 'searchBy',
      type: filterType.TEXT,
      value: '',
      applyFilter: false,
      enable: true,
      searchBy: true,
    },
  ],
  filterListLoading: false,
  isFilterUpdated: false,
  sortField: 'createdAt',
  sortBy: 'desc',
};

const invoiceSlice = createSlice({
  name: 'Invoice',
  initialState,
  reducers: {
    /**
     * Update filter
     * @param {any} state - state
     * @param {any} action - action
     */
    updateInvoiceListFilter: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      }
    },
    /**
     * Update sort
     * @param {any} state - state
     * @param {any} action - action
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: string }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },

  /**
   * Add extra reducers here
   * @param {ActionReducerMapBuilder<InvoiceInitialProperties>} builder - Reducer builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<InvoiceInitialProperties>) => {
    builder
      /**
       * Pending state for fetching filters
       * @param {InvoiceInitialProperties} state - The current state
       */
      .addCase(fetchInvoiceFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      /**
       * Fulfilled state for fetching filters
       * @param {InvoiceInitialProperties} state - The current state
       */
      .addCase(fetchInvoiceFilters.fulfilled, (state) => {
        state.filterListLoading = false;
      })
      /**
       * Rejected state for fetching filters
       * @param {InvoiceInitialProperties} state - The current state
       */
      .addCase(fetchInvoiceFilters.rejected, (state) => {
        state.filterListLoading = false;
      })
      /**
       * Pending state for fetching invoice list
       * @param {InvoiceInitialProperties} state - The current state
       */
      .addCase(fetchInvoiceList.pending, (state) => {
        state.loading = true;
      })
      /**
       * Fulfilled state for fetching invoice list
       * @param {InvoiceInitialProperties} state - The current state
       * @param {PayloadAction<APIInvoiceResponseInterface>} action - The action payload
       */
      .addCase(fetchInvoiceList.fulfilled, (state, action) => {
        state.loading = false;
        state.invoices = (action.payload.results || []).map((invoice: any) => ({
          ...invoice,
          id: invoice._id,
        }));
        state.page = action.payload.page || 1;
        state.limit = action.payload.limit || 20;
        state.totalPages = action.payload.totalPages || 1;
        state.totalResults = action.payload.totalResults || 0;
      })
      /**
       * Rejected state for fetching invoice list
       * @param {InvoiceInitialProperties} state - The current state
       */
      .addCase(fetchInvoiceList.rejected, (state) => {
        state.loading = false;
      });
  },
});

export const { updateInvoiceListFilter, setSortData } = invoiceSlice.actions;

export default invoiceSlice.reducer;
